import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TemplateService } from '../template.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ITemplate } from '../template.interface';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import { TemplateType, TemplateTypeLabels } from 'src/app/common/enums/template-type';

@Component({
  selector: 'app-template-detail',
  standalone: true,
  imports: [
    CommonModule,
    TablerIconsModule,
    TranslateModule,
    MaterialModule,
    ReactiveFormsModule
  ],
  templateUrl: './template-detail.component.html',
  styleUrl: './template-detail.component.scss'
})
export class TemplateDetailComponent implements OnInit, OnDestroy {

  templateForm!: FormGroup;
  isEdit: boolean = false;
  isLoading: boolean = false;
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  templateTypeList = Object.values(TemplateType).filter(x => typeof x === 'number') as TemplateType[];
  templateTypeLabels = TemplateTypeLabels;

  constructor(
    private _templateService: TemplateService,
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<TemplateDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private _data: { template?: ITemplate, isEdit?: boolean, templateType?: TemplateType },
  ) {
    this.isEdit = _data?.isEdit || false;
  }

  ngOnInit(): void {
    // Get templateType from data (either from template object or directly from data)
    const templateType = this._data?.template?.templateType || this._data?.templateType || null;

    this.templateForm = this._formbuilder.group({
      templateName: [this._data?.template?.templateName || '', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      templateType: [templateType, [Validators.required]],
      templateDescription: [this._data?.template?.templateDescription || '', [Validators.required, Validators.maxLength(500)]]
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddTemplate() {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const data = this.templateForm.getRawValue();

    this._templateService.createTemplate(data).pipe(
      takeUntil(this._unsubscribeAll),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (response) => {
        if (response?.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.TEMPLATE')));
        }
      },
      error: (err) => {
        if (err?.error?.errors) {
          const allErrors = Object.values(err.error.errors).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      }
    });
  }

  onUpdateTemplate() {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const data = {
      id: this._data?.template?.id || undefined,
      ...this.templateForm.getRawValue()
    };

    this._templateService.updateTemplate(data).pipe(
      takeUntil(this._unsubscribeAll),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (response) => {
        if (response?.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.TEMPLATE')));
        }
      },
      error: (err) => {
        if (err?.error?.errors) {
          const allErrors = Object.values(err.error.errors).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

}
